import { Component, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, first, map, Observable, switchMap, take } from 'rxjs';
import { MalwareFilterPolicy } from '../../../../../../../interfaces/powershell/exo/malware-filter-policy.interface';
import {
    AntiMalwarePolicy_Schema,
    generateAntiMawarePolicySchema,
    malwareFileTypesBestPractices,
} from '../../../../../models/baseline-schemas/anti-malware-policy.schema';
import { BaseComponent } from '../../base/base.component';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { QuarantinePolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/quarantine-policy/model';
import { selectSession } from 'projects/angular-clarity/src/app/stores/root.store';

const fileTypeActionLabels: Record<string, string> = {
    Quarantine: 'Quarantine the message',
    Reject: 'Reject the message with an NDR',
};
@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit, OnChanges {
    defaultPolicy: MalwareFilterPolicy;
    override: string[] = [];
    fileTypeActionLabels = fileTypeActionLabels;
    @ViewChild('desiredFileTypeValueRef') desiredFileTypeValueRef;

    fileTypesDesiredValuePopup: string[] = [];
    desiredValueTemp: string;
    emailsLookups: string[];
    quarantinePolicyLookup$: Observable<QuarantinePolicy[]>;

    tenant_id$: Observable<string> = this.store.pipe(
        select(selectSession),
        map((sess) => sess.session.clientId),
    );

    constructor(
        private dialog: MatDialog,
        private fb: FormBuilder,
        private store: Store<any>,
    ) {
        super();
    }

    ngOnInit(): void {
        const schema: AntiMalwarePolicy_Schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.defaultPolicy = this.data.find((res) => res.IsDefault);
        this.override = this.baselineTemplateItem?.override || [];
        this.form = this.initForm(schema);

        this.setCustomNotificationValidators(this.formValue.CustomNotifications);
        this.setAdminAddressValidators(
            'ExternalSenderAdminAddress',
            this.formValue.EnableExternalSenderAdminNotifications,
        );
        this.setAdminAddressValidators(
            'InternalSenderAdminAddress',
            this.formValue.EnableInternalSenderAdminNotifications,
        );

        const hasError = this.form.invalid;
        this.baselineErrorChange.next({
            remediate: hasError,
            save: hasError,
        });

        this.quarantinePolicyLookup$ = this.getQuarantinePolicy();

        this.getMailboxLookup().subscribe((res) => {
            this.emailsLookups = res;
        });

        this.form
            .get('EnableFileFilter')
            .valueChanges.pipe(distinctUntilChanged())
            .subscribe((enabled: boolean) => {
                if (enabled) {
                    const currentTypes = this.form.get('FileTypes').value as string[];
                    if (!currentTypes || currentTypes.length === 0) {
                        if (!currentTypes || currentTypes.length === 0) {
                            this.desiredFileTypeValue = malwareFileTypesBestPractices.map((t) => t.toLowerCase());
                        }
                    }
                }
            });

        this.form.valueChanges.subscribe((value) => {
            const updatedSchema = generateAntiMawarePolicySchema(this.formValue);

            this.setCustomNotificationValidators(this.formValue.CustomNotifications);
            this.setAdminAddressValidators(
                'ExternalSenderAdminAddress',
                this.formValue.EnableExternalSenderAdminNotifications,
            );
            this.setAdminAddressValidators(
                'InternalSenderAdminAddress',
                this.formValue.EnableInternalSenderAdminNotifications,
            );

            const hasError = this.form.invalid;
            this.baselineErrorChange.next({
                remediate: hasError,
                save: hasError,
            });

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    initForm(schema: AntiMalwarePolicy_Schema) {
        return this.fb.group({
            ZapEnabled: [schema?.contains?.properties?.ZapEnabled?.const ?? null],
            EnableFileFilter: [schema?.contains?.properties?.EnableFileFilter?.const],
            FileTypes: [schema?.contains?.properties?.FileTypes?.items?.enum || []],
            FileTypeAction: [schema?.contains?.properties?.FileTypeAction?.const || 'Quarantine'],
            QuarantineTag: [schema?.contains?.properties?.QuarantineTag?.const],
            EnableInternalSenderAdminNotifications: [
                schema?.contains?.properties?.EnableInternalSenderAdminNotifications?.const,
            ],
            InternalSenderAdminAddress: [schema?.contains?.properties?.InternalSenderAdminAddress?.const],
            EnableExternalSenderAdminNotifications: [
                schema?.contains?.properties?.EnableExternalSenderAdminNotifications?.const,
            ],
            ExternalSenderAdminAddress: [schema?.contains?.properties?.ExternalSenderAdminAddress?.const],
            CustomNotifications: [schema?.contains?.properties?.CustomNotifications?.const ?? null],
            CustomFromName: [schema?.contains?.properties?.CustomFromName?.const],
            CustomFromAddress: [schema?.contains?.properties?.CustomFromAddress?.const],
            CustomInternalSubject: [schema?.contains?.properties?.CustomInternalSubject?.const],
            CustomInternalBody: [schema?.contains?.properties?.CustomInternalBody?.const],
            CustomExternalSubject: [schema?.contains?.properties?.CustomExternalSubject?.const],
            CustomExternalBody: [schema?.contains?.properties?.CustomExternalBody?.const],
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.defaultPolicy = this.data.find((res) => res.IsDefault);
    }

    private setAdminAddressValidators(field: string, enabled: boolean): void {
        const ctrl = this.form.get(field);
        if (enabled) {
            ctrl.setValidators([Validators.required, Validators.email]);
        } else {
            ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private setCustomNotificationValidators(enabled: boolean): void {
        const textFields = [
            'CustomFromName',
            'CustomInternalSubject',
            'CustomInternalBody',
            'CustomExternalSubject',
            'CustomExternalBody',
        ];
        const addressField = this.form.get('CustomFromAddress');

        textFields.forEach((name) => {
            const ctrl = this.form.get(name);
            if (enabled) {
                ctrl.setValidators([Validators.required]);
            } else {
                ctrl.clearValidators();
            }
            ctrl.updateValueAndValidity({ emitEvent: false });
        });

        if (enabled) {
            addressField.setValidators([Validators.required, Validators.email]);
        } else {
            addressField.clearValidators();
        }
        addressField.updateValueAndValidity({ emitEvent: false });
    }

    getQuarantinePolicy(): Observable<QuarantinePolicy[]> {
        return this.tenant_id$.pipe(
            switchMap((tid) =>
                combineLatest([
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.all),
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.status),
                ]),
            ),
            first(([data, status]) => status.loaded),
            map(([data]) => data),
        );
    }

    getMailboxLookup() {
        return this.tenant_id$.pipe(
            switchMap((tid) =>
                combineLatest([
                    this.store.select(client(tid).graph.users.withMailbox),
                    this.store.select(client(tid).graph.users.status),
                ]),
            ),
            first(([data, status]) => status.loaded),
            map(([data]) => data),
            map((res) => res.map((re) => re['mail'])),
            take(1),
        );
    }

    onSelectEmail(e: string): void {
        this.form.get('InternalSenderAdminAddress').setValue(e);
    }

    onSelectExternalEmail(e: string): void {
        this.form.get('ExternalSenderAdminAddress').setValue(e);
    }

    updateDesiredFileTypeValue(): void {
        this.fileTypesDesiredValuePopup = this.desiredFileTypeValue.map((res) => res);

        this.dialog.open(this.desiredFileTypeValueRef, {
            width: '700px',
            height: '700px',
        });
    }

    addNewDesiredFileTypeValue(): void {
        const trimmedValue = this.desiredValueTemp?.trim();

        if (trimmedValue && !this.fileTypesDesiredValuePopup.includes(trimmedValue)) {
            this.fileTypesDesiredValuePopup.push(trimmedValue);
        }

        this.desiredValueTemp = '';
    }

    removeNewDesiredFileTypeValue(item: string): void {
        const index = this.fileTypesDesiredValuePopup.indexOf(item);
        if (index > -1) {
            this.fileTypesDesiredValuePopup.splice(index, 1);
        }
    }

    updateDesiredFileTypeValueToBestPractices(): void {
        this.desiredFileTypeValue = malwareFileTypesBestPractices.map((res) => res.toLocaleLowerCase());
    }

    clearAllDesiredFileTypes(): void {
        this.fileTypesDesiredValuePopup = [];
    }

    saveDesiredFileTypeValue(): void {
        this.desiredFileTypeValue = this.fileTypesDesiredValuePopup.map((res) => res.toLocaleLowerCase());
        this.dialog.closeAll();
    }

    get desiredFileType(): FormControl {
        return this.form.get('FileTypes') as FormControl;
    }

    get desiredFileTypeValue(): string[] {
        return this.desiredFileType.value;
    }

    set desiredFileTypeValue(value: string[]) {
        this.desiredFileType.setValue(value);
    }

    get formValue() {
        return this.form.getRawValue();
    }
}
